import { useMemo } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { TextCell } from "@similarweb/pro-logic-components";
import { TitlePlaceholderLoader, Tooltip } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { FiltersChip } from "app/pages/sales-intelligence/common-components/filters-panel";
import { useOptedInIndustryTaxonomy } from "app/pages/sales-intelligence/modules/industry/hooks/useOptedInIndustryTaxonomy";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { createPluralHelperForI18nKey } from "app/pages/sales-intelligence/utils/strings";

import type { TeamListItem } from "../../types/teams.types";
import { buildTeamFiltersSummaryLines } from "../TeamFiltersTooltip/buildTeamFiltersSummaryLines";
import { TeamFiltersTooltipContent } from "../TeamFiltersTooltip/TeamFiltersTooltipContent";
import { useTeamFilterTitles } from "../TeamFiltersTooltip/teamFilterTitlesContext";

import { navigateOnPlainClick } from "./navigateOnPlainClick";

const getFiltersCountKey = createPluralHelperForI18nKey("si.management.teams.column.territory_filters.count", true);

const StyledFiltersCellLoader = styled(TitlePlaceholderLoader)`
    max-height: 24px;
    max-width: 60px;
`;

type TeamsTableFiltersCellProps = {
    team: TeamListItem;
};

export const TeamsTableFiltersCell = ({ team }: TeamsTableFiltersCellProps) => {
    const { translate } = useTranslate();

    const { byKey, isReady } = useTeamFilterTitles();
    const industryTaxonomy = useOptedInIndustryTaxonomy();

    const summaryLines = useMemo(
        () => buildTeamFiltersSummaryLines(team.filters, byKey, industryTaxonomy),
        [team.filters, byKey, industryTaxonomy],
    );

    // The count and the tooltip both come from the summary, so the chip only ever promises filters the
    // tooltip can name — a team can carry a saved filter this surface does not expose. Until the config
    // resolves nothing can be named, and an early "-" would read as "no filters".
    if (!isReady) {
        return <StyledFiltersCellLoader id={`teams-table-filters-loader-${team.teamId}`} />;
    }

    const filtersCount = summaryLines.length;

    if (filtersCount === 0) {
        return <TextCell>-</TextCell>;
    }

    return (
        <Tooltip
            placement="top"
            content={
                <TeamFiltersTooltipContent
                    lines={summaryLines}
                    dataAutomation={`teams-table-filters-tooltip-${team.teamId}`}
                />
            }
        >
            <Box
                as="a"
                display="inline-flex"
                textDecoration="none"
                href={siNavigator.createTeamDetailLink({ id: team.teamId, tab: "filters" })}
                onClick={(event) =>
                    navigateOnPlainClick(event, () => {
                        void siNavigator.goToTeamDetailPage({ id: team.teamId, tab: "filters" });
                    })
                }
                data-automation={`teams-table-filters-${team.teamId}`}
            >
                <FiltersChip
                    text={translate(getFiltersCountKey(filtersCount), { count: filtersCount })}
                    hideActionButton
                />
            </Box>
        </Tooltip>
    );
};
